import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModalById } from 'hooks/useRecordModalById';
import { GatewayRuleMappingMethod } from 'pages/GatewayRules/components/GatewayRuleMapping/GatewayRuleMappingMethod';
import { GatewayRuleMappingMethodSwitch } from 'pages/GatewayRules/components/GatewayRuleMapping/GatewayRuleMappingMethodSwitch';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { Typography } from 'shared/mui/Typography';
import { MethodsShowDialog } from 'shared/widgets/MethodsShowDialog/MethodsShowDialog';
import { Mapping } from 'types/embedded/gatewayRule';
import { Method } from 'types/embedded/method';
import { Permission } from 'types/embedded/permission';

import { GatewayRuleMappingAdditionalFields } from './GatewayRuleMappingAdditionalFields';
import { StyledContainer, StyledFieldsContainer } from './GatewayRuleMappingInfo.styled';

type Props = {
  actionsSlot?: React.ReactNode;
  enableAdditionalFields?: boolean;
};

export const GatewayRuleMappingInfo: React.FC<Props> = ({
  actionsSlot,
  enableAdditionalFields,
}) => {
  const translate = useTypedTranslate();
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();

  const mappingRecord = useRecordContext<Mapping>();
  const method = mappingRecord?.method;
  const methodModal = useRecordModalById<Method>({ resource: Resources.Methods });

  return (
    <StyledContainer>
      <GatewayRuleMappingMethod methodLogo={method?.logo} />
      <Box>
        <StyledFieldsContainer>
          <Box sx={{ textWrap: 'nowrap' }}>
            <Typography component="span" mr={1} variant="subtitle2">
              {`${translate('app.table.method')}:`}
            </Typography>
            {hasResourceAccess(Resources.Methods) && method?.name && (
              <Typography
                component="span"
                onClick={() => methodModal.handleOpenRecordModal(method?.['@id'])}
                sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                variant="body2"
              >
                {method.name}
              </Typography>
            )}
          </Box>
          <Box sx={{ textWrap: 'nowrap' }}>
            <Typography component="span" mr={1} variant="subtitle2">
              {`${translate('app.table.direction')}:`}
            </Typography>
            <Typography component="span" variant="body2">
              {mappingRecord?.direction?.description}
            </Typography>
          </Box>
          {enableAdditionalFields && <GatewayRuleMappingAdditionalFields />}
        </StyledFieldsContainer>
      </Box>
      <Box alignItems="center" display="flex" gap={1} sx={{ marginLeft: 'auto' }}>
        {actionsSlot}
        {hasResourcePermissions('mappings', [Permission.Full, Permission.Patch]) && (
          <GatewayRuleMappingMethodSwitch />
        )}
      </Box>
      {methodModal.openRecordModal && (
        <MethodsShowDialog
          isFetching={methodModal.isFetching || !methodModal.record}
          method={methodModal.record}
          onClose={methodModal.handleCloseRecordModal}
          open={methodModal.openRecordModal}
        />
      )}
    </StyledContainer>
  );
};
